import { mayCarry, mayRunCellar, type CellarSummary } from "@corklog/shared";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import { fetchCellar } from "./api.js";

/**
 * Task 9.2 — who is reading this cellar, available to every screen in it.
 *
 * **One fetch above the routes rather than one per screen.** The role decides
 * what a page draws, so a page that had to ask for it would draw itself twice
 * on every navigation: once guessing and once corrected. It is held here, keyed
 * by the cellar in the address bar, and it survives moving between the sections
 * of one cellar because this provider sits above `<Routes>` and does not
 * remount when the route below it changes.
 *
 * **Primed from the list on the way in.** Opening a cellar from the front page
 * has already been told the role — it is in the list response — so the cache
 * below is written there and read here, and the common path knows who is
 * reading before it renders anything. A link opened cold still costs one
 * request, which is why the state has an `unknown` that is not `guest`: a row
 * of sections that guessed would either flash links a guest may not follow, or
 * hide the owner's own links from them for a moment on every page.
 */

/** Null while the answer has not arrived, or outside a cellar entirely. */
const CellarContext = createContext<CellarSummary | null>(null);

/**
 * What the list already knew, so the next screen does not ask again.
 *
 * Module-level and never invalidated by time: a role changes when somebody is
 * invited or revoked, which is a write this browser makes or a fact it learns
 * on its next load, and a stale entry can at worst draw a link that the API
 * then refuses. Keyed by cellar because a user may own more than one — the
 * second one is a summer house, not a hypothetical.
 */
const known = new Map<string, CellarSummary>();

export function rememberCellars(cellars: readonly CellarSummary[]): void {
  for (const cellar of cellars) known.set(cellar.id, cellar);
}

/**
 * Forgotten on sign-out, with the cached cellar on the phone's disk.
 *
 * What is held here is *this* person's role in a cellar, and the next person
 * to sign in on the same browser may have a different one — or none. Without
 * this, the first page they opened would draw itself from the answer somebody
 * else was given, until the fetch behind it came back. Stale by a minute is
 * fine; stale by a whole other user is not.
 */
export function forgetCellars(): void {
  known.clear();
  try {
    sessionStorage.removeItem(LAST_CELLAR);
  } catch {
    // Nothing remembered, then, which is what forgetting asks for.
  }
}

/**
 * The cellar last opened in this tab, for the pages that stand in none.
 *
 * *Upptäck* and *Rapporter och önskemål* belong to no cellar, but since
 * 2026-10-07 the way between pages is a cellar's row and its *Mer* — so a page
 * outside every cellar draws the menu of the one the reader came from. Per
 * tab, in `sessionStorage`, for the reason the wine list's narrowing is: two
 * tabs may be in two cellars, and a choice that outlived the browser would be
 * met a week later as a menu for the wrong house. Forgotten on sign-out with
 * the rest, since the next person may not reach it.
 */
const LAST_CELLAR = "corklog.lastCellar";

function noteLastCellar(cellarId: string): void {
  try {
    sessionStorage.setItem(LAST_CELLAR, cellarId);
  } catch {
    // Not remembered; the pages outside a cellar draw a menu without one.
  }
}

function lastCellarId(): string | null {
  try {
    return sessionStorage.getItem(LAST_CELLAR);
  } catch {
    return null;
  }
}

/**
 * The last cellar, for a page outside every cellar: its id and, once known,
 * what this reader is in it. `settled` once there is nothing more to wait
 * for — no cellar remembered, the summary in hand, or the fetch failed, in
 * which case the page draws a menu without the cellar rather than none.
 * Inert when `wanted` is false, so a cellar's own pages pay nothing for it.
 */
export function useLastCellar(wanted: boolean): {
  id: string | null;
  cellar: CellarSummary | null;
  settled: boolean;
} {
  const [id] = useState(() => (wanted ? lastCellarId() : null));
  const [cellar, setCellar] = useState<CellarSummary | null>(() =>
    id === null ? null : (known.get(id) ?? null),
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (id === null || known.has(id)) return;
    let cancelled = false;
    void fetchCellar(id)
      .then((found) => {
        known.set(id, found);
        if (!cancelled) setCellar(found);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (failed) return { id: null, cellar: null, settled: true };
  return { id, cellar, settled: id === null || cellar !== null };
}

/** The cellar in the address bar, or null on a page that is not in one. */
export function cellarIdOf(pathname: string): string | null {
  const match = /^\/kallare\/([^/]+)/.exec(pathname);
  return match?.[1] ?? null;
}

export function CellarProvider({
  cellarId,
  children,
}: {
  cellarId: string | null;
  children: ReactNode;
}) {
  const [cellar, setCellar] = useState<CellarSummary | null>(() =>
    cellarId === null ? null : (known.get(cellarId) ?? null),
  );

  useEffect(() => {
    if (cellarId === null) {
      setCellar(null);
      return;
    }
    noteLastCellar(cellarId);

    // Shown from the cache first so that arriving from the list draws once,
    // and re-fetched anyway: the cached copy is what the list was told, which
    // may be a minute or a week old.
    const cached = known.get(cellarId) ?? null;
    setCellar(cached);

    let cancelled = false;
    void fetchCellar(cellarId)
      .then((found) => {
        known.set(cellarId, found);
        if (!cancelled) setCellar(found);
      })
      .catch(() => {
        // Left as it stands. A cellar that cannot be reached is a screen that
        // will say so for itself, and a failed request here must not be the
        // thing that decides somebody is a guest.
      });

    return () => {
      cancelled = true;
    };
  }, [cellarId]);

  return <CellarContext value={cellar}>{children}</CellarContext>;
}

/** The cellar being read, or null until it is known. */
export function useCellar(): CellarSummary | null {
  return useContext(CellarContext);
}

/**
 * Whether the screen may draw the things only the cellar's owner does.
 *
 * `null` is *not yet known* and every caller treats it as such rather than as
 * a no: what a guest must not see is withheld by the API, and what this
 * governs is whether a button is worth drawing. Hidden rather than disabled,
 * per `project-scope.md` — a disabled button on a page a friend is browsing is
 * an invitation to ask why, and there is no good answer.
 *
 * **It asked whether the reader was not a guest until 2026-09-21**, which was
 * the same question while `Contributor` sat unbuilt in the enum. It is not any
 * more: a husgäst carries a bottle out and does not keep the books, so cataloguing
 * a wine, moving bottles between shelves, correcting a departure and undo are
 * all withheld from them. The rule is `mayRunCellar` in `shared/`, the same
 * one the API enforces — a button drawn where the route refuses is the *try
 * again* that can never work.
 */
export function useMayWrite(): boolean | null {
  const cellar = useCellar();
  return cellar === null ? null : mayRunCellar(cellar.role);
}

/**
 * Whether the screen may draw the carrying of a bottle.
 *
 * Taking one out to a dinner, bringing it back, and asking what would suit
 * what is being cooked. The owner and the husgäst; not a guest.
 *
 * **Drinking is not among them, from 2026-09-21.** Recording a bottle as drunk
 * is `useMayWrite`, with the rest of the cellar's bookkeeping — the husgäst
 * carries the bottle to the dinner and the owner writes down that it is gone.
 *
 * A second hook rather than a parameter on the first, because every caller of
 * that one is asking a question with a fixed answer and a boolean argument at
 * a call site says nothing about which question was meant.
 */
export function useMayCarry(): boolean | null {
  const cellar = useCellar();
  return cellar === null ? null : mayCarry(cellar.role);
}

/**
 * Whether this reader may be told what the wine cost.
 *
 * Resolved by the server against the role and the cellar's own setting, so
 * this is a fact and not a rule to be re-applied. `null` while unknown, which
 * lets a screen hold a price back rather than explain the wrong silence: a
 * missing figure is *not recorded* to a reader entitled to it and *not shown*
 * to one who is not, and the two sentences must not be swapped.
 */
export function useSeesPrices(): boolean | null {
  const cellar = useCellar();
  return cellar === null ? null : cellar.seesPrices;
}
